import { useEffect, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { CartItem } from "@/hooks/useCart";
import { Json } from "@/integrations/supabase/types";
import { MARKET } from "@/lib/market";
import { cartStorageKey, shouldSyncCartToServer } from "@/lib/marketScope";

const CART_LOADED_KEY = "hexatech_cart_loaded_from_db";
const CART_STORAGE_KEY = cartStorageKey(MARKET);
// user_carts has no market column: only PT carts are stored server-side.
const SYNC_TO_SERVER = shouldSyncCartToServer(MARKET);

export function useCartPersistence(
  items: CartItem[],
  setItems: React.Dispatch<React.SetStateAction<CartItem[]>>
) {
  const { user } = useAuth();
  const isInitialLoad = useRef(true);
  const isSyncing = useRef(false);
  const previousItemsLength = useRef(items.length);

  // Load cart from database when user logs in
  useEffect(() => {
    const loadCartFromDatabase = async () => {
      if (!SYNC_TO_SERVER) {
        // BR cart is local-only (already hydrated from its own key); mark as loaded so checkout behaves as in PT.
        isInitialLoad.current = false;
        if (user) sessionStorage.setItem(CART_LOADED_KEY, user.id); else sessionStorage.removeItem(CART_LOADED_KEY);
        return;
      }
      if (!user) {
        // User logged out - clear the session flag but keep localStorage for anonymous browsing
        sessionStorage.removeItem(CART_LOADED_KEY);
        isInitialLoad.current = true;
        return;
      }

      // Check if this is a different user than before (user switch)
      const lastLoadedUserId = sessionStorage.getItem(CART_LOADED_KEY);
      const isNewUser = lastLoadedUserId && lastLoadedUserId !== user.id;
      
      // If same user already loaded in this session, skip
      if (lastLoadedUserId === user.id) {
        isInitialLoad.current = false;
        return;
      }

      try {
        isSyncing.current = true;
        
        // Fetch the user's cart from database
        const { data, error } = await supabase
          .from("user_carts")
          .select("cart_items")
          .eq("user_id", user.id)
          .single();

        if (!error && data && data.cart_items) {
          const savedItems = data.cart_items as unknown as CartItem[];
          // REPLACE local cart with database cart (not merge)
          setItems(savedItems);
          // Also update localStorage to match
          localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(savedItems));
        } else {
          // No saved cart in DB - always start fresh after login
          // This prevents anonymous browsing cart from carrying over
          setItems([]);
          localStorage.setItem(CART_STORAGE_KEY, JSON.stringify([]));
        }

        sessionStorage.setItem(CART_LOADED_KEY, user.id);
      } catch (error) {
        console.error("Error loading cart from database:", error);
      } finally {
        isSyncing.current = false;
        isInitialLoad.current = false;
      }
    };

    loadCartFromDatabase();
  }, [user, setItems]);

  // Save cart to database when items change
  useEffect(() => {
    const saveCartToDatabase = async () => {
      if (!SYNC_TO_SERVER || !user || isInitialLoad.current || isSyncing.current) return;

      try {
        const cartData = {
          user_id: user.id,
          cart_items: JSON.parse(JSON.stringify(items)) as Json,
        };
        
        await supabase
          .from("user_carts")
          .upsert(cartData, { onConflict: "user_id" });
      } catch (error) {
        console.error("Error saving cart to database:", error);
      }
    };

    const itemsRemoved = items.length < previousItemsLength.current;
    previousItemsLength.current = items.length;

    if (itemsRemoved) {
      saveCartToDatabase();
    } else {
      const timeoutId = setTimeout(saveCartToDatabase, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [items, user]);
}
